import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { routes, api } from '../../../utils/constants';
import { GET, PATCH } from '../../../configs/api';
import { getUser } from '../../../store/slices/userSlice';
import { getUsersList } from '../../../store/slices/usersSlice';
import { getClients } from '../../../store/slices/clientsSlice';
import { formatUserDisplayName } from '../../../utils/userDisplayName';
import {
  canEditJobNotes as canEditJobNotesPermission,
  canMarkJobPaid,
  isOnlyDispatcher,
  primaryRole,
  ROLES,
} from '../../../utils/permissions';
import JobDetailsDialog from '../jobs/JobDetailsDialog';
import { getStatusLabel, getStatusChipStyles } from '../../../utils/jobStatuses';
import { getPriorityLabel } from '../../../utils/jobPriorities';
import { getClientChipStyles } from '../../../utils/clientColors';
import DateRangePickerDropdown from '../../../components/DateRangePickerDropdown';
import { useJobStatusColorsRevision } from '../../../hooks/useJobStatusColorsRevision';
import useIsMobile from '../../../hooks/useIsMobile';
import { scaleColumnsForMobile } from '../../../utils/mobileDataGrid';
import {
  GRID_PREFERENCES_STORAGE_KEY,
  DEFAULT_GRID_PREFERENCE,
  parseGridPreferences,
  sanitizeGridPreference,
  applyColumnOrder,
  AccountingColumnsPopover,
} from './AccountingColumnsHelper';
import AccountingSavedSearchesBar from './AccountingSavedSearchesBar';
import {
  arraysEqualById,
  captureFilterSnapshot,
  clearDefaultSearch,
  deleteSearch,
  EMPTY_FILTER_SNAPSHOT,
  findSearchById,
  getDefaultSearchForView,
  getViewBucket,
  loadAccountingSavedSearches,
  parseDateField,
  pruneSavedSearchesAgainstClients,
  renameSearch,
  sanitizeFilterSnapshot,
  setDefaultSearch,
  snapshotsMatch,
  upsertSearch,
} from './AccountingSavedSearchesHelper';
import { jobUtcDateMatchesRange } from '../../../utils/accountingDateFilters';

const PATH_TO_VIEW = {
  [routes.accountingReadyForInvoicing]: 'ready-for-invoicing',
  [routes.accountingInvoiced]: 'invoiced',
  [routes.accountingInvoicedLoss]: 'invoiced-loss',
  [routes.accountingNotQBInvoiced]: 'not-qb-invoiced',
  [routes.accountingNotSentToClient]: 'not-sent-to-client',
  [routes.accountingFactored]: 'factored',
  [routes.accountingNonFactored]: 'non-factored',
  [routes.accountingOpenInvoices]: 'open-invoices',
  [routes.accountingPaid]: 'paid',
};

/** Filter criteria shown below each accounting view title (must match API getAccountingJobs). */
const VIEW_FILTER_DESCRIPTIONS = {
  'ready-for-invoicing': 'Status: Ready for Invoicing, with no invoiced proposals.',
  invoiced: 'At least one proposal marked as invoiced.',
  'invoiced-loss': 'Status: Invoiced/Loss.',
  'not-qb-invoiced': 'Status: Invoiced or Invoiced/Loss, not yet marked as QuickBooks invoiced.',
  'not-sent-to-client': 'Status: Invoiced, Invoiced/Loss, or Recall No Charge, not yet sent to the client.',
  factored: 'Factored jobs with at least one invoiced proposal.',
  'non-factored': 'Non-factored jobs with at least one invoiced proposal.',
  'open-invoices': 'Unpaid jobs with at least one invoiced proposal.',
  paid: 'Jobs marked as paid, with at least one invoiced proposal.',
};

const paginationModel = { page: 0, pageSize: 30 };
const VIEW_MODE_STORAGE_KEY = 'accountingViewMode';
const VALID_VIEW_MODES = new Set(['description', 'ar-ap']);

const readViewModePreference = () => {
  const storedValue = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return VALID_VIEW_MODES.has(storedValue) ? storedValue : 'ar-ap';
};

const parseNteValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    if (!cleaned) return 0;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const parseGridNumber = (value) => {
  if (value == null || value === '') return 0;
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const numericFieldValueGetter = (field) => (_value, row) => parseGridNumber(row[field]);

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString()}`;

const DATE_PICKER_SX = {
  width: { xs: '100%', sm: 260 },
  minWidth: { xs: '100%', sm: 220 },
};

const getTechnicianDisplayName = (tech) => {
  if (!tech) return '';
  if (tech.type === 'vendor') {
    return tech.company_name || '';
  }
  return `${tech.first_name || ''} ${tech.last_name || ''}`.trim();
};

const getJobTechnicianNames = (job) => {
  if (!Array.isArray(job?.technicians)) return [];
  return job.technicians.map(getTechnicianDisplayName).filter(Boolean);
};

const resolveSelectedRowIds = (selectionModel, rows) => {
  if (!selectionModel?.ids) return [];
  if (selectionModel.type === 'include') {
    return Array.from(selectionModel.ids);
  }
  const excludedIds = selectionModel.ids;
  return rows.map((row) => row.id).filter((id) => !excludedIds.has(id));
};

const getAccountingJobSearchValues = (job, displayUserByEmail, viewMode) => {
  const nteValue = parseNteValue(job?.nte);
  const locationParts = [];
  if (job?.city) locationParts.push(job.city);
  if (job?.state) locationParts.push(job.state);

  const baseValues = [
    job?.work_order,
    job?.invoice_number,
    job?.description,
    job?.notes,
    job?.client_name,
    locationParts.join(', '),
    job?.city,
    job?.state,
    job?.address,
    job?.status != null ? getStatusLabel(Number(job.status)) : '',
    job?.priority != null ? getPriorityLabel(job.priority) : '',
    job?.trade,
    displayUserByEmail(job?.admin),
    job?.admin,
    displayUserByEmail(job?.dispatcher),
    job?.dispatcher,
    job?.created_at ? job.created_at.slice(0, 10) : '',
    job?.invoiced_date ? job.invoiced_date.slice(0, 10) : '',
    formatCurrency(nteValue),
    String(nteValue),
    getJobTechnicianNames(job).join(', '),
  ];

  if (viewMode !== 'ar-ap') return baseValues;

  return [
    ...baseValues,
    formatCurrency(parseGridNumber(job?.technicians_cost)),
    String(parseGridNumber(job?.technicians_cost)),
    formatCurrency(parseGridNumber(job?.parts_cost)),
    String(parseGridNumber(job?.parts_cost)),
    formatCurrency(parseGridNumber(job?.total_cost)),
    String(parseGridNumber(job?.total_cost)),
    formatCurrency(parseGridNumber(job?.profit)),
    String(parseGridNumber(job?.profit)),
  ];
};

const jobMatchesSearchQuery = (job, query, displayUserByEmail, viewMode) =>
  getAccountingJobSearchValues(job, displayUserByEmail, viewMode)
    .some((value) => value != null && String(value).toLowerCase().includes(query));

function Accounting() {
  const location = useLocation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useIsMobile();
  const user = useSelector(getUser);
  const companyId = user?.company_id ?? null;
  const isPrimaryAdmin = primaryRole(user) === ROLES.ADMIN;
  const usersList = useSelector(getUsersList);
  const clientsData = useSelector(getClients);
  // Ready only after a successful load — error payloads set loading=false with clients=[].
  const clientsReady = clientsData?.loading === false && !clientsData?.error;
  const currentPath = location.pathname;
  const view = PATH_TO_VIEW[currentPath] || 'ready-for-invoicing';
  const statusColorsRevision = useJobStatusColorsRevision();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [initialJobDetailsTabValue, setInitialJobDetailsTabValue] = useState(0);
  const [selectedDateStart, setSelectedDateStart] = useState(null);
  const [selectedDateEnd, setSelectedDateEnd] = useState(null);
  const [selectedInvoicedDateStart, setSelectedInvoicedDateStart] = useState(null);
  const [selectedInvoicedDateEnd, setSelectedInvoicedDateEnd] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnsPanelAnchorEl, setColumnsPanelAnchorEl] = useState(null);
  const [gridPreferences, setGridPreferences] = useState(() => parseGridPreferences());
  const [viewMode, setViewMode] = useState(readViewModePreference); // 'description' | 'ar-ap'
  const [savedSearchesStorage, setSavedSearchesStorage] = useState(() => loadAccountingSavedSearches(companyId));
  const [activeSavedSearchId, setActiveSavedSearchId] = useState(null);
  const [loadingPaid, setLoadingPaid] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [paidConfirmDialogOpen, setPaidConfirmDialogOpen] = useState(false);
  const [pendingPaidJobIds, setPendingPaidJobIds] = useState([]);
  const [pendingPaidValue, setPendingPaidValue] = useState(null);
  const [batchPaidUpdating, setBatchPaidUpdating] = useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [editingNotesJobId, setEditingNotesJobId] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesUpdating, setNotesUpdating] = useState(false);
  const [notesDialogError, setNotesDialogError] = useState('');
  const prevSelectedJobRef = useRef(null);
  const suppressCustomDetectionRef = useRef(false);
  const loadedCompanyIdRef = useRef(companyId);

  const canTogglePaid = useMemo(
    () => canMarkJobPaid(user),
    [user],
  );

  const canEditJobNotes = useMemo(
    () => canEditJobNotesPermission(user),
    [user],
  );

  const displayUserByEmail = useMemo(() => {
    const byEmail = new Map(
      (usersList || [])
        .filter((u) => u.email)
        .map((u) => [u.email.toLowerCase(), u]),
    );
    return (email) => {
      if (!email) return '';
      const match = byEmail.get(String(email).toLowerCase());
      return match ? formatUserDisplayName(match) : email;
    };
  }, [usersList]);

  const applyJobsResult = (result) => {
    if (result.status === 403) {
      setError('Access denied. Only accountants, owners, general managers, and admins can access this.');
      setJobs([]);
      return;
    }
    if (result.error) {
      setError(result.message || 'Something went wrong. Please try again later.');
      setJobs([]);
      return;
    }
    setError(null);
    setJobs(result.data?.jobs ?? []);
  };

  const currentViewBucket = useMemo(
    () => getViewBucket(savedSearchesStorage, view),
    [savedSearchesStorage, view],
  );

  // Client filter options: dispatchers don't have clients in Redux, so derive from jobs
  const clientFilterOptions = useMemo(() => {
    if (isOnlyDispatcher(user)) {
      const seen = new Set();
      return (jobs || [])
        .filter((job) => {
          if (!job.client_id || seen.has(job.client_id)) return false;
          seen.add(job.client_id);
          return true;
        })
        .map((job) => ({ id: job.client_id, client_name: job.client_name || 'Unknown' }))
        .sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
    }
    return clientsData.clients || [];
  }, [user, jobs, clientsData.clients]);

  const getCurrentFilterSnapshot = useCallback(() => captureFilterSnapshot({
    searchQuery,
    selectedClients,
    selectedDateStart,
    selectedDateEnd,
    selectedInvoicedDateStart,
    selectedInvoicedDateEnd,
  }), [
    searchQuery,
    selectedClients,
    selectedDateStart,
    selectedDateEnd,
    selectedInvoicedDateStart,
    selectedInvoicedDateEnd,
  ]);

  // Strict client sanitize only for primary-role admins once clients have loaded successfully.
  const sanitizeClientOptions = isPrimaryAdmin && clientsReady ? clientFilterOptions : undefined;

  const applyFilterSnapshot = useCallback((snapshot) => {
    const sanitized = sanitizeFilterSnapshot(snapshot, {
      clientOptions: sanitizeClientOptions,
    });

    suppressCustomDetectionRef.current = true;
    setSearchQuery(sanitized.searchQuery);
    setSelectedClients(sanitized.selectedClients);
    setSelectedDateStart(parseDateField(sanitized.selectedDateStart));
    setSelectedDateEnd(parseDateField(sanitized.selectedDateEnd));
    setSelectedInvoicedDateStart(parseDateField(sanitized.selectedInvoicedDateStart));
    setSelectedInvoicedDateEnd(parseDateField(sanitized.selectedInvoicedDateEnd));
  }, [sanitizeClientOptions]);

  const handleResetFilters = useCallback(() => {
    applyFilterSnapshot(EMPTY_FILTER_SNAPSHOT);
    setActiveSavedSearchId(null);
  }, [applyFilterSnapshot]);

  const handleApplySavedSearch = useCallback((searchId) => {
    const search = findSearchById(savedSearchesStorage, view, searchId);
    if (!search) return;
    applyFilterSnapshot(search.filters);
    setActiveSavedSearchId(search.id);
  }, [savedSearchesStorage, view, applyFilterSnapshot]);

  const handleSaveSearch = useCallback(({ name, setAsDefault }) => {
    if (!companyId) return;
    const { storage: nextStorage, searchId } = upsertSearch(companyId, savedSearchesStorage, view, {
      name,
      filters: getCurrentFilterSnapshot(),
      setAsDefault,
    });
    setSavedSearchesStorage(nextStorage);
    setActiveSavedSearchId(searchId);
  }, [companyId, savedSearchesStorage, view, getCurrentFilterSnapshot]);

  const handleDeleteSavedSearch = useCallback((searchId) => {
    if (!companyId) return;
    const nextStorage = deleteSearch(companyId, savedSearchesStorage, view, searchId);
    setSavedSearchesStorage(nextStorage);
    if (activeSavedSearchId === searchId) {
      setActiveSavedSearchId(null);
    }
  }, [companyId, savedSearchesStorage, view, activeSavedSearchId]);

  const handleRenameSavedSearch = useCallback((searchId, name) => {
    if (!companyId) return;
    const nextStorage = renameSearch(companyId, savedSearchesStorage, view, searchId, name);
    setSavedSearchesStorage(nextStorage);
  }, [companyId, savedSearchesStorage, view]);

  const handleSetDefaultSavedSearch = useCallback((searchId) => {
    if (!companyId) return;
    const nextStorage = setDefaultSearch(companyId, savedSearchesStorage, view, searchId);
    setSavedSearchesStorage(nextStorage);
  }, [companyId, savedSearchesStorage, view]);

  const handleClearDefaultSavedSearch = useCallback(() => {
    if (!companyId) return;
    const nextStorage = clearDefaultSearch(companyId, savedSearchesStorage, view);
    setSavedSearchesStorage(nextStorage);
  }, [companyId, savedSearchesStorage, view]);

  // Reload company-scoped saved searches when the active company changes.
  useEffect(() => {
    if (loadedCompanyIdRef.current === companyId) return;
    loadedCompanyIdRef.current = companyId;
    const storage = loadAccountingSavedSearches(companyId);
    setSavedSearchesStorage(storage);
    const defaultSearch = getDefaultSearchForView(storage, view);
    applyFilterSnapshot(defaultSearch ? defaultSearch.filters : EMPTY_FILTER_SNAPSHOT);
    setActiveSavedSearchId(defaultSearch ? defaultSearch.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Primary-role admin: drop inaccessible clients from this company's saved searches.
  useEffect(() => {
    if (!isPrimaryAdmin || !clientsReady || !companyId) return;
    if (loadedCompanyIdRef.current !== companyId) return;

    const pruned = pruneSavedSearchesAgainstClients(
      companyId,
      loadAccountingSavedSearches(companyId),
      clientFilterOptions,
    );
    setSavedSearchesStorage(pruned.storage);

    suppressCustomDetectionRef.current = true;
    setSelectedClients((prev) => {
      const next = sanitizeFilterSnapshot(
        { selectedClients: prev },
        { clientOptions: clientFilterOptions },
      ).selectedClients;
      if (arraysEqualById(prev, next)) return prev;
      return next;
    });
  }, [isPrimaryAdmin, clientsReady, clientFilterOptions, companyId]);

  useEffect(() => {
    const storage = loadAccountingSavedSearches(companyId);
    setSavedSearchesStorage(storage);
    const defaultSearch = getDefaultSearchForView(storage, view);
    const snapshot = defaultSearch ? defaultSearch.filters : EMPTY_FILTER_SNAPSHOT;
    const sanitized = sanitizeFilterSnapshot(snapshot, {
      clientOptions: sanitizeClientOptions,
    });

    suppressCustomDetectionRef.current = true;
    setSearchQuery(sanitized.searchQuery);
    setSelectedClients(sanitized.selectedClients);
    setSelectedDateStart(parseDateField(sanitized.selectedDateStart));
    setSelectedDateEnd(parseDateField(sanitized.selectedDateEnd));
    setSelectedInvoicedDateStart(parseDateField(sanitized.selectedInvoicedDateStart));
    setSelectedInvoicedDateEnd(parseDateField(sanitized.selectedInvoicedDateEnd));
    setActiveSavedSearchId(defaultSearch ? defaultSearch.id : null);
    // Only re-apply when the accounting route view changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  useEffect(() => {
    if (suppressCustomDetectionRef.current) {
      suppressCustomDetectionRef.current = false;
      return;
    }
    if (!activeSavedSearchId) return;

    const activeSearch = findSearchById(savedSearchesStorage, view, activeSavedSearchId);
    if (!activeSearch) {
      setActiveSavedSearchId(null);
      return;
    }

    const sanitizedCurrent = sanitizeFilterSnapshot(getCurrentFilterSnapshot(), {
      clientOptions: sanitizeClientOptions,
    });
    if (!snapshotsMatch(sanitizedCurrent, activeSearch.filters)) {
      setActiveSavedSearchId(null);
    }
  }, [
    searchQuery,
    selectedClients,
    selectedDateStart,
    selectedDateEnd,
    selectedInvoicedDateStart,
    selectedInvoicedDateEnd,
    activeSavedSearchId,
    savedSearchesStorage,
    view,
    sanitizeClientOptions,
    getCurrentFilterSnapshot,
  ]);

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    setRowSelectionModel({ type: 'include', ids: new Set() });
  }, [view]);

  const handleOpenNotesDialog = useCallback((job) => {
    setEditingNotesJobId(job.id);
    setNotesDraft(job.notes ?? '');
    setNotesDialogError('');
    setIsNotesDialogOpen(true);
  }, []);

  const handleCloseNotesDialog = useCallback(() => {
    if (notesUpdating) return;
    setIsNotesDialogOpen(false);
    setEditingNotesJobId(null);
    setNotesDraft('');
    setNotesDialogError('');
  }, [notesUpdating]);

  const handleUpdateNotes = useCallback(async () => {
    if (!editingNotesJobId) return;

    setNotesUpdating(true);
    setNotesDialogError('');
    const { data, status, message } = await PATCH(
      `${api.updateJobNotes}/${editingNotesJobId}/notes`,
      {},
      { notes: notesDraft },
    );

    if (status === 200 && data) {
      setJobs((prev) => prev.map((job) => (job.id === editingNotesJobId
        ? { ...job, notes: data.notes ?? notesDraft }
        : job)));
      if (selectedJob?.id === editingNotesJobId) {
        setSelectedJob((prev) => (prev ? { ...prev, notes: data.notes ?? notesDraft } : prev));
      }
      setIsNotesDialogOpen(false);
      setEditingNotesJobId(null);
      setNotesDraft('');
      setNotesDialogError('');
      setNotesUpdating(false);
      return;
    }

    setNotesDialogError(message || 'Failed to update notes. Please try again.');
    setNotesUpdating(false);
  }, [editingNotesJobId, notesDraft, selectedJob]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetchJobs = async () => {
      const result = await GET(api.getAccountingJobs, { view });
      if (cancelled) return;
      setLoading(false);
      applyJobsResult(result);
    };
    fetchJobs();
    return () => { cancelled = true; };
  }, [view]);

  useEffect(() => {
    const wasOpen = prevSelectedJobRef.current != null;
    const isNowClosed = selectedJob == null;
    prevSelectedJobRef.current = selectedJob;

    if (!wasOpen || !isNowClosed) return undefined;

    let cancelled = false;
    const refetchJobs = async () => {
      const result = await GET(api.getAccountingJobs, { view });
      if (cancelled) return;
      applyJobsResult(result);
    };
    refetchJobs();
    return () => { cancelled = true; };
  }, [selectedJob, view]);

  const refetchAccountingJobs = useCallback(async () => {
    const result = await GET(api.getAccountingJobs, { view });
    applyJobsResult(result);
  }, [view]);

  const performTogglePaid = useCallback(async (jobId, newValue, { skipRefetch = false } = {}) => {
    setLoadingPaid((prev) => ({ ...prev, [jobId]: true }));
    try {
      const response = await PATCH(`${api.updatePaid}/${jobId}/paid`, {}, { value: newValue });

      if (!response.error && response.status === 200 && response.data) {
        if (selectedJob?.id === jobId) {
          setSelectedJob(response.data);
        }
        if (!skipRefetch) {
          await refetchAccountingJobs();
        }
        return { success: true };
      }

      return { success: false, message: response.message || 'Failed to update paid status.' };
    } catch {
      return { success: false, message: 'Failed to update paid status.' };
    } finally {
      setLoadingPaid((prev) => ({ ...prev, [jobId]: false }));
    }
  }, [selectedJob, refetchAccountingJobs]);

  const handleTogglePaidSingle = useCallback(async (jobId, currentPaid) => {
    if (!canTogglePaid) return;
    await performTogglePaid(jobId, !currentPaid);
  }, [canTogglePaid, performTogglePaid]);

  const performBatchSetPaid = useCallback(async (jobIds, paidValue) => {
    if (!canTogglePaid || jobIds.length === 0 || paidValue == null) return;

    setBatchPaidUpdating(true);
    try {
      const results = await Promise.all(
        jobIds.map((jobId) => performTogglePaid(jobId, paidValue, { skipRefetch: true })),
      );

      const failedCount = results.filter((result) => !result?.success).length;
      if (failedCount > 0) {
        console.error(`Failed to update paid status for ${failedCount} job(s).`);
      }

      await refetchAccountingJobs();
      setRowSelectionModel({ type: 'include', ids: new Set() });
    } finally {
      setBatchPaidUpdating(false);
      setPaidConfirmDialogOpen(false);
      setPendingPaidJobIds([]);
      setPendingPaidValue(null);
    }
  }, [canTogglePaid, performTogglePaid, refetchAccountingJobs]);

  const handlePaidConfirmDialogCancel = useCallback(() => {
    if (batchPaidUpdating) return;
    setPaidConfirmDialogOpen(false);
    setPendingPaidJobIds([]);
    setPendingPaidValue(null);
  }, [batchPaidUpdating]);

  const handlePaidConfirmDialogConfirm = useCallback(() => {
    performBatchSetPaid(pendingPaidJobIds, pendingPaidValue);
  }, [performBatchSetPaid, pendingPaidJobIds, pendingPaidValue]);

  const renderNotesCell = useCallback((params) => {
    const notes = params.value?.trim();
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, height: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!notes ? (
            <Typography sx={{ color: 'text.secondary' }}>
              -
            </Typography>
          ) : (
            <Tooltip title={notes} arrow placement="top">
              <Typography
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {notes}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {canEditJobNotes && (
          <Tooltip title="Edit notes" arrow placement="top">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenNotesDialog(params.row);
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
              aria-label="Edit notes"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canEditJobNotes, handleOpenNotesDialog]);

  const renderTechniciansCell = useCallback((params) => {
    const names = getJobTechnicianNames(params.row);
    if (names.length === 0) {
      return (
        <Typography sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', height: '100%' }}>
          —
        </Typography>
      );
    }

    const primaryName = names[0];
    const extraCount = names.length - 1;
    const content = (
      <Typography
        component="span"
        noWrap
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          minWidth: 0,
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {primaryName}
        </Box>
        {extraCount > 0 && (
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              ml: 0.5,
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.8125rem',
            }}
          >
            +{extraCount}
          </Box>
        )}
      </Typography>
    );

    if (names.length === 1) {
      return content;
    }

    return (
      <Tooltip title={names.join(', ')} arrow placement="top">
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minWidth: 0, maxWidth: '100%' }}>
          {content}
        </Box>
      </Tooltip>
    );
  }, []);

  const renderPaidCell = useCallback((params) => {
    const isLoading = loadingPaid[params.row.id];
    const isPaid = params.row.paid || false;

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <CircularProgress size={20} />
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%', height: '100%' }}>
        <Switch
          checked={isPaid}
          onChange={() => handleTogglePaidSingle(params.row.id, isPaid)}
          disabled={!canTogglePaid}
          size="small"
          onClick={(event) => event.stopPropagation()}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#4caf50',
              '& + .MuiSwitch-track': {
                backgroundColor: '#4caf50',
              },
            },
            '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
              backgroundColor: '#4caf50',
            },
            '& .MuiSwitch-track': {
              backgroundColor: isDarkMode ? '#475569' : '#ccc',
            },
          }}
        />
        <Typography variant="body2" sx={{ color: isPaid ? '#4caf50' : 'text.secondary' }}>
          {isPaid ? 'Yes' : 'No'}
        </Typography>
      </Box>
    );
  }, [loadingPaid, canTogglePaid, handleTogglePaidSingle, isDarkMode]);

  const getSectionTitle = () => {
    switch (currentPath) {
      case routes.accountingReadyForInvoicing: return 'Ready for Invoicing';
      case routes.accountingInvoiced: return 'Invoiced';
      case routes.accountingInvoicedLoss: return 'Invoiced/Loss';
      case routes.accountingNotQBInvoiced: return 'Not QB Invoiced';
      case routes.accountingNotSentToClient: return 'Not Sent';
      case routes.accountingFactored: return 'Factored';
      case routes.accountingNonFactored: return 'Non Factored';
      case routes.accountingOpenInvoices: return 'Unpaid Invoices';
      case routes.accountingPaid: return 'Paid Invoices';
      default: return 'Accounting';
    }
  };

  const columns = useMemo(() => [
    {
      field: 'work_order',
      headerName: 'Work Order',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {params.row?.is_locked && (
            <Tooltip title="Job locked - read only" arrow placement="top">
              <LockIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice #',
      width: 130,
      valueFormatter: (value) => value || '—',
    },
    {
      field: 'invoiced_date',
      headerName: 'Invoice Date',
      width: 130,
      valueFormatter: (value) => (value ? value.slice(0, 10) : '—'),
    },
    { field: 'description', headerName: 'Description', width: 250 },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 240,
      renderCell: renderNotesCell,
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 150,
      renderCell: (params) => {
        const clientId = params.row.client_id;
        const label = params.value || 'Unknown';
        const { bgColor, textColor, borderColor } = getClientChipStyles(clientId);
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      },
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 200,
      valueGetter: (value, row) => {
        const parts = [];
        if (row.city) parts.push(row.city);
        if (row.state) parts.push(row.state);
        return parts.join(', ');
      },
    },
    { field: 'address', headerName: 'Address', width: 250 },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const statusId = Number(params.value);
        const label = getStatusLabel(statusId);
        const { bgColor, textColor, borderColor } = getStatusChipStyles(statusId);

        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      },
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 110,
      renderCell: (params) => {
        const priority = params.value;
        const label = getPriorityLabel(priority);
        let bgColor = '';
        let textColor = '';
        let borderColor = '';
        if (priority == '1') {
          bgColor = isDarkMode ? '#7f1d1d' : '#fee2e2';
          textColor = isDarkMode ? '#fca5a5' : '#991b1b';
          borderColor = '#ef4444';
        } else if (priority == '2') {
          bgColor = isDarkMode ? '#78350f' : '#fef3c7';
          textColor = isDarkMode ? '#fbbf24' : '#92400e';
          borderColor = '#f59e0b';
        } else if (priority == '3') {
          bgColor = isDarkMode ? '#1e3a8a' : '#dbeafe';
          textColor = isDarkMode ? '#93c5fd' : '#1e3a8a';
          borderColor = '#3b82f6';
        } else if (priority == '4') {
          bgColor = isDarkMode ? '#374151' : '#e5e7eb';
          textColor = isDarkMode ? '#d1d5db' : '#374151';
          borderColor = '#6b7280';
        } else {
          bgColor = isDarkMode ? '#374151' : '#e5e7eb';
          textColor = isDarkMode ? '#d1d5db' : '#374151';
          borderColor = '#6b7280';
        }
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      },
    },
    { field: 'trade', headerName: 'Trade', width: 120 },
    {
      field: 'admin',
      headerName: 'Admin',
      width: 150,
      valueFormatter: (value) => displayUserByEmail(value),
    },
    {
      field: 'dispatcher',
      headerName: 'Dispatcher',
      width: 150,
      valueFormatter: (value) => displayUserByEmail(value),
    },
    {
      field: 'created_at',
      headerName: 'Start Date',
      width: 130,
      valueFormatter: (value) => (value ? value.slice(0, 10) : ''),
    },
    {
      field: 'nte',
      headerName: 'NTE',
      width: 130,
      renderCell: (params) => {
        const numValue = parseNteValue(params.value);
        const formattedValue = formatCurrency(numValue);
        const isPositive = numValue > 0;
        return (
          <Typography
            sx={{
              color: isPositive ? '#22c55e' : 'text.primary',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {formattedValue}
          </Typography>
        );
      },
    },
    {
      field: 'paid',
      headerName: 'Paid',
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => (row.paid ? 'Yes' : 'No'),
      renderCell: renderPaidCell,
    },
  ], [isDarkMode, statusColorsRevision, displayUserByEmail, renderPaidCell, renderNotesCell]);

  const arApColumns = useMemo(() => [
    {
      field: 'client_name',
      headerName: 'Client',
      width: 150,
      renderCell: (params) => {
        const clientId = params.row.client_id;
        const label = params.value || 'Unknown';
        const { bgColor, textColor, borderColor } = getClientChipStyles(clientId);
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              },
            }}
          />
        );
      },
    },
    {
      field: 'work_order',
      headerName: 'Work Order',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {params.row?.is_locked && (
            <Tooltip title="Job locked - read only" arrow placement="top">
              <LockIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 240,
      renderCell: renderNotesCell,
    },
    {
      field: 'technicians',
      headerName: 'Technicians',
      width: 180,
      sortable: false,
      valueGetter: (_value, row) => getJobTechnicianNames(row).join(', '),
      renderCell: renderTechniciansCell,
    },
    {
      field: 'created_at',
      headerName: 'Create Date',
      width: 130,
      valueFormatter: (value) => (value ? value.slice(0, 10) : ''),
    },
    {
      field: 'invoiced_date',
      headerName: 'Invoiced Date',
      width: 130,
      valueFormatter: (value) => (value ? value.slice(0, 10) : '—'),
    },
    {
      field: 'nte',
      headerName: 'NTE',
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('nte'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        return (
          <Typography
            sx={{
              color: '#22c55e',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {formatCurrency(numValue)}
          </Typography>
        );
      },
    },
    {
      field: 'total_cost',
      headerName: 'Total Cost',
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('total_cost'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        return (
          <Typography
            sx={{
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {formatCurrency(numValue)}
          </Typography>
        );
      },
    },
    {
      field: 'technicians_cost',
      headerName: 'Technicians Cost',
      width: 150,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('technicians_cost'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        return (
          <Typography
            sx={{
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {formatCurrency(numValue)}
          </Typography>
        );
      },
    },
    {
      field: 'parts_cost',
      headerName: 'Parts Cost',
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('parts_cost'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        return (
          <Typography
            sx={{
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {formatCurrency(numValue)}
          </Typography>
        );
      },
    },
    {
      field: 'profit',
      headerName: 'Profit',
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('profit'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        const isPositive = numValue >= 0;
        return (
          <Typography
            sx={{
              color: isPositive ? '#22c55e' : '#ef4444',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {formatCurrency(numValue)}
          </Typography>
        );
      },
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice #',
      width: 130,
      valueFormatter: (value) => value || '—',
    },
    {
      field: 'paid',
      headerName: 'Paid',
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => (row.paid ? 'Yes' : 'No'),
      renderCell: renderPaidCell,
    },
  ], [renderNotesCell, renderTechniciansCell, renderPaidCell, statusColorsRevision]);

  const descriptionFields = useMemo(() => columns.map((column) => column.field), [columns]);
  const arApFields = useMemo(() => arApColumns.map((column) => column.field), [arApColumns]);

  const sanitizedGridPreferences = useMemo(() => ({
    description: sanitizeGridPreference(gridPreferences.description ?? DEFAULT_GRID_PREFERENCE, descriptionFields),
    'ar-ap': sanitizeGridPreference(gridPreferences['ar-ap'] ?? DEFAULT_GRID_PREFERENCE, arApFields),
  }), [gridPreferences, descriptionFields, arApFields]);

  const activeColumns = viewMode === 'ar-ap' ? arApColumns : columns;
  const activeFields = viewMode === 'ar-ap' ? arApFields : descriptionFields;
  const activeGridPreference = sanitizedGridPreferences[viewMode];

  const columnsByField = useMemo(
    () => new Map(activeColumns.map((column) => [column.field, column])),
    [activeColumns],
  );

  const orderedColumns = useMemo(
    () => applyColumnOrder(activeColumns, activeGridPreference.columnOrder),
    [activeColumns, activeGridPreference.columnOrder],
  );

  const displayColumns = useMemo(
    () => (isMobile ? scaleColumnsForMobile(orderedColumns) : orderedColumns),
    [isMobile, orderedColumns],
  );

  const columnsPanelOpen = Boolean(columnsPanelAnchorEl);

  useEffect(() => {
    window.localStorage.setItem(GRID_PREFERENCES_STORAGE_KEY, JSON.stringify(sanitizedGridPreferences));
  }, [sanitizedGridPreferences]);

  const updateActiveGridPreference = useCallback((updater) => {
    setGridPreferences((prev) => {
      const currentPreference = sanitizeGridPreference(prev?.[viewMode] ?? DEFAULT_GRID_PREFERENCE, activeFields);
      const nextPreference = typeof updater === 'function' ? updater(currentPreference) : updater;
      return {
        ...prev,
        [viewMode]: sanitizeGridPreference(nextPreference, activeFields),
      };
    });
  }, [viewMode, activeFields]);

  const handleColumnsPanelToggle = (event) => {
    setColumnsPanelAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleColumnsPanelClose = () => {
    setColumnsPanelAnchorEl(null);
  };

  const handleColumnVisibilityModelChange = useCallback((model) => {
    updateActiveGridPreference((currentPreference) => ({
      ...currentPreference,
      columnVisibilityModel: model,
    }));
  }, [updateActiveGridPreference]);

  const handleColumnVisibilityToggle = useCallback((field) => {
    updateActiveGridPreference((currentPreference) => {
      const isVisible = currentPreference.columnVisibilityModel[field] !== false;
      return {
        ...currentPreference,
        columnVisibilityModel: {
          ...currentPreference.columnVisibilityModel,
          [field]: !isVisible,
        },
      };
    });
  }, [updateActiveGridPreference]);

  const handleReorderColumns = useCallback((activeField, overField) => {
    updateActiveGridPreference((currentPreference) => {
      const nextOrder = [...currentPreference.columnOrder];
      const fromIndex = nextOrder.indexOf(activeField);
      const toIndex = nextOrder.indexOf(overField);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return currentPreference;

      const [movedField] = nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, movedField);
      return {
        ...currentPreference,
        columnOrder: nextOrder,
      };
    });
  }, [updateActiveGridPreference]);

  const handleResetColumns = useCallback(() => {
    updateActiveGridPreference({
      columnOrder: activeFields,
      columnVisibilityModel: {},
    });
  }, [updateActiveGridPreference, activeFields]);

  const filteredJobs = useMemo(() => {
    let filtered = jobs;

    if (selectedClients.length > 0) {
      const selectedClientIds = selectedClients.map((client) => client.id);
      filtered = filtered.filter((job) =>
        (job.client_id && selectedClientIds.includes(job.client_id))
        || (job.client_name && selectedClients.some(
          (client) => (client.client_name || '').toLowerCase() === (job.client_name || '').toLowerCase(),
        )));
    }

    filtered = filtered.filter((job) => (
      jobUtcDateMatchesRange(job.created_at, selectedDateStart, selectedDateEnd)
      && jobUtcDateMatchesRange(job.invoiced_date, selectedInvoicedDateStart, selectedInvoicedDateEnd)
    ));

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((job) => jobMatchesSearchQuery(job, query, displayUserByEmail, viewMode));
    }

    return filtered;
  }, [
    jobs,
    selectedClients,
    selectedDateStart,
    selectedDateEnd,
    selectedInvoicedDateStart,
    selectedInvoicedDateEnd,
    searchQuery,
    displayUserByEmail,
    viewMode,
  ]);

  const jobsCount = filteredJobs.length;
  const totalNte = useMemo(
    () => filteredJobs.reduce((sum, job) => sum + parseNteValue(job?.nte), 0),
    [filteredJobs],
  );
  const { totalPaidNte, totalUnpaidNte } = useMemo(() => {
    let paidSum = 0;
    let unpaidSum = 0;
    filteredJobs.forEach((job) => {
      const nte = parseNteValue(job?.nte);
      if (job?.paid) paidSum += nte;
      else unpaidSum += nte;
    });
    return { totalPaidNte: paidSum, totalUnpaidNte: unpaidSum };
  }, [filteredJobs]);

  const selectedJobIds = useMemo(
    () => resolveSelectedRowIds(rowSelectionModel, filteredJobs),
    [rowSelectionModel, filteredJobs],
  );
  const selectedPaidFlipCount = selectedJobIds.length;

  const handleBatchSetPaidClick = useCallback((paidValue) => {
    if (!canTogglePaid || selectedJobIds.length === 0) return;

    if (selectedJobIds.length > 1) {
      setPendingPaidJobIds(selectedJobIds);
      setPendingPaidValue(paidValue);
      setPaidConfirmDialogOpen(true);
      return;
    }

    performBatchSetPaid(selectedJobIds, paidValue);
  }, [canTogglePaid, selectedJobIds, performBatchSetPaid]);

  const handleRowClick = useCallback((params) => {
    setInitialJobDetailsTabValue(0);
    setSelectedJob(params.row);
  }, []);

  const hasDateFilters = Boolean(
    selectedDateStart
    || selectedDateEnd
    || selectedInvoicedDateStart
    || selectedInvoicedDateEnd,
  );

  const handleResetDateFilter = () => {
    setSelectedDateStart(null);
    setSelectedDateEnd(null);
    setSelectedInvoicedDateStart(null);
    setSelectedInvoicedDateEnd(null);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Accounting - {getSectionTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {VIEW_FILTER_DESCRIPTIONS[view]}
        </Typography>
        <Paper
          sx={{
            p: 3,
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'error.main',
            fontWeight: 600,
          }}
        >
          {error}
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: { xs: 1, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', md: '2.125rem' } }}>
              Accounting - {getSectionTitle()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {VIEW_FILTER_DESCRIPTIONS[view]}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <AccountingSavedSearchesBar
              variant="header"
              accountingView={view}
              savedSearches={currentViewBucket.searches}
              defaultSearchId={currentViewBucket.defaultId}
              activeSearchId={activeSavedSearchId}
              onApplySearch={handleApplySavedSearch}
              onSaveSearch={handleSaveSearch}
              onDeleteSearch={handleDeleteSavedSearch}
              onRenameSearch={handleRenameSavedSearch}
              onSetDefault={handleSetDefaultSavedSearch}
              onClearDefault={handleClearDefaultSavedSearch}
              onResetFilters={handleResetFilters}
              isDarkMode={isDarkMode}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  minWidth: 140,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: isDarkMode ? '#166534' : '#22c55e',
                  backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.14)' : 'rgba(34, 197, 94, 0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, width: '100%' }}>
                  {view === 'paid' ? 'Total Paid' : view === 'open-invoices' ? 'Total Unpaid' : 'Total NTE'}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 800,
                    mt: 0.25,
                    lineHeight: 1.2,
                    color: isDarkMode ? '#86efac' : '#166534',
                    fontVariantNumeric: 'tabular-nums',
                    width: '100%',
                  }}
                >
                  {formatCurrency(totalNte)}
                </Typography>
              </Box>
              {view === 'invoiced' && (
                <>
                  <Box
                    sx={{
                      minWidth: 140,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: isDarkMode ? '#166534' : '#22c55e',
                      backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.14)' : 'rgba(34, 197, 94, 0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, width: '100%' }}>
                      Total Paid
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 800,
                        mt: 0.25,
                        lineHeight: 1.2,
                        color: isDarkMode ? '#86efac' : '#166534',
                        fontVariantNumeric: 'tabular-nums',
                        width: '100%',
                      }}
                    >
                      {formatCurrency(totalPaidNte)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      minWidth: 140,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: isDarkMode ? '#b45309' : '#f59e0b',
                      backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.14)' : 'rgba(245, 158, 11, 0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, width: '100%' }}>
                      Total Unpaid
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 800,
                        mt: 0.25,
                        lineHeight: 1.2,
                        color: isDarkMode ? '#fbbf24' : '#b45309',
                        fontVariantNumeric: 'tabular-nums',
                        width: '100%',
                      }}
                    >
                      {formatCurrency(totalUnpaidNte)}
                    </Typography>
                  </Box>
                </>
              )}
              <Box
                sx={{
                  minWidth: 100,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.08)' : 'rgba(100, 116, 139, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, width: '100%' }}>
                  Jobs
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, mt: 0.25, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, width: '100%' }}
                >
                  {jobsCount.toLocaleString()}
                </Typography>
              </Box>
            </Box>
            <Autocomplete
              multiple
              size="small"
              options={clientFilterOptions}
              value={selectedClients}
              onChange={(event, newValue) => {
                setSelectedClients(newValue);
              }}
              getOptionLabel={(option) => ((typeof option === 'object' && option?.client_name) ? option.client_name : String(option))}
              isOptionEqualToValue={(option, value) => ((option?.id && value?.id) ? option.id === value.id : option === value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Clients"
                  placeholder={selectedClients.length > 0 ? '' : 'Select clients...'}
                  sx={{
                    minWidth: { xs: '100%', sm: 180 },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  const { bgColor, textColor, borderColor } = getClientChipStyles(option?.id);
                  return (
                    <Chip
                      key={key}
                      label={option?.client_name || option}
                      size="small"
                      {...chipProps}
                      sx={{
                        backgroundColor: bgColor,
                        color: textColor,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        height: '24px',
                        borderRadius: '6px',
                        border: `1px solid ${borderColor}`,
                        '& .MuiChip-label': {
                          padding: '0 8px',
                        },
                        '& .MuiChip-deleteIcon': {
                          fontSize: '16px',
                          ...(isDarkMode && {
                            color: textColor,
                            '&:hover': {
                              color: textColor,
                            },
                          }),
                        },
                      }}
                    />
                  );
                })}
              renderOption={(props, option) => (
                <li {...props}>
                  {option?.client_name || option}
                </li>
              )}
              sx={{
                minWidth: { xs: '100%', sm: 180 },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <DateRangePickerDropdown
                label="Invoiced Date"
                placeholder="Filter by invoiced date"
                startDate={selectedInvoicedDateStart}
                endDate={selectedInvoicedDateEnd}
                onStartChange={setSelectedInvoicedDateStart}
                onEndChange={setSelectedInvoicedDateEnd}
                isDarkMode={isDarkMode}
                sx={DATE_PICKER_SX}
              />
              <DateRangePickerDropdown
                label="Created Date"
                placeholder="Filter by created date"
                startDate={selectedDateStart}
                endDate={selectedDateEnd}
                onStartChange={setSelectedDateStart}
                onEndChange={setSelectedDateEnd}
                isDarkMode={isDarkMode}
                sx={DATE_PICKER_SX}
              />
              <Button
                variant="outlined"
                onClick={handleResetDateFilter}
                disabled={!hasDateFilters}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 0 }}>
          <TextField
            fullWidth
            placeholder="Search jobs by any column..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
                height: '42px',
                '& input::placeholder': {
                  color: 'text.secondary',
                  opacity: 1,
                },
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                  borderWidth: '1px',
                },
              },
            }}
          />
        </Box>

        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          mt: 0.5,
          mb: 1.25,
        }}>
          {isMobile && (
            <IconButton
              onClick={handleColumnsPanelToggle}
              aria-label="Manage columns"
              size="small"
              sx={{
                border: '1px solid',
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                borderRadius: 1,
              }}
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(event, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
              }
            }}
            size="small"
            sx={{
              height: '42px',
            }}
          >
            <ToggleButton
              value="description"
              sx={{
                px: { xs: 1.5, md: 3 },
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: viewMode === 'description' ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                backgroundColor: viewMode === 'description' ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                '&:hover': {
                  backgroundColor: viewMode === 'description'
                    ? (isDarkMode ? '#e0e0e0' : '#333')
                    : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                },
                '&.Mui-selected': {
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#000' : '#fff',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  },
                },
              }}
            >
              Description
            </ToggleButton>
            <ToggleButton
              value="ar-ap"
              sx={{
                px: { xs: 1.5, md: 3 },
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: viewMode === 'ar-ap' ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                backgroundColor: viewMode === 'ar-ap' ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                '&:hover': {
                  backgroundColor: viewMode === 'ar-ap'
                    ? (isDarkMode ? '#e0e0e0' : '#333')
                    : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                },
                '&.Mui-selected': {
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#000' : '#fff',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  },
                },
              }}
            >
              AR-AP
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Paper
          elevation={0}
          sx={{
            height: 'calc(100vh - 260px)',
            width: '100%',
            p: { xs: 0.5, md: 2 },
            backgroundColor: 'background.default',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              overflow: 'hidden',
              position: 'relative',
              height: '100%',
              paddingRight: isMobile ? 0 : '48px',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '48px',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                backgroundColor: 'background.paper',
                borderLeft: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              }}
            >
              <Button
                onClick={handleColumnsPanelToggle}
                sx={{
                  minWidth: '48px',
                  width: '48px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: '12px 0',
                  gap: 1,
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '3px',
                    backgroundColor: isDarkMode ? '#fff' : '#000',
                  },
                }}
                aria-label="Manage columns"
              >
                <ViewColumnIcon sx={{ fontSize: '20px', mb: 0.5, color: 'text.primary' }} />
                <Typography
                  sx={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    color: 'text.primary',
                  }}
                >
                  Columns
                </Typography>
              </Button>
            </Box>
            )}

            <AccountingColumnsPopover
              open={columnsPanelOpen}
              anchorEl={columnsPanelAnchorEl}
              onClose={handleColumnsPanelClose}
              isDarkMode={isDarkMode}
              activeGridPreference={activeGridPreference}
              columnsByField={columnsByField}
              onToggleColumnVisibility={handleColumnVisibilityToggle}
              onReorderColumns={handleReorderColumns}
              onResetColumns={handleResetColumns}
            />

            {canTogglePaid && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  minHeight: 52,
                  flexShrink: 0,
                  borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {selectedPaidFlipCount} work order{selectedPaidFlipCount === 1 ? '' : 's'} selected
                </Typography>
                {selectedPaidFlipCount > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setRowSelectionModel({ type: 'include', ids: new Set() })}
                      disabled={batchPaidUpdating}
                      sx={{
                        textTransform: 'none',
                        borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                        color: 'text.secondary',
                      }}
                    >
                      Clear selection
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleBatchSetPaidClick(true)}
                      disabled={batchPaidUpdating}
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#4caf50',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#43a047',
                        },
                      }}
                    >
                      {batchPaidUpdating ? 'Updating...' : `Mark as Paid (${selectedPaidFlipCount})`}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleBatchSetPaidClick(false)}
                      disabled={batchPaidUpdating}
                      sx={{
                        textTransform: 'none',
                        backgroundColor: isDarkMode ? '#fff' : '#000',
                        color: isDarkMode ? '#000' : '#fff',
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        },
                      }}
                    >
                      {batchPaidUpdating ? 'Updating...' : `Mark as Unpaid (${selectedPaidFlipCount})`}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <DataGrid
                rows={filteredJobs}
                loading={loading}
                columns={displayColumns}
                density={isMobile ? 'compact' : 'standard'}
                columnVisibilityModel={activeGridPreference.columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                getRowId={(row) => row.id}
                initialState={{ pagination: { paginationModel } }}
                pageSizeOptions={[5, 10, 20, 30, 50, 100]}
                checkboxSelection={canTogglePaid}
                keepNonExistentRowsSelected
                disableRowSelectionOnClick
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={setRowSelectionModel}
                onRowClick={handleRowClick}
                sx={{
                  border: 0,
                  height: '100%',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                    borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #f5f5f5',
                  },
                  '& .MuiDataGrid-cell[data-field="description"]': {
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                    },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                    backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                    position: 'relative',
                    zIndex: 2,
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
      <JobDetailsDialog
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        user={user}
        initialTabValue={initialJobDetailsTabValue}
      />

      <Dialog
        open={paidConfirmDialogOpen}
        onClose={handlePaidConfirmDialogCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            backgroundColor: 'background.paper',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {pendingPaidValue ? 'Mark as Paid' : 'Mark as Unpaid'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to mark {pendingPaidJobIds.length} work order{pendingPaidJobIds.length === 1 ? '' : 's'} as {pendingPaidValue ? 'paid' : 'unpaid'}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handlePaidConfirmDialogCancel}
            disabled={batchPaidUpdating}
            variant="outlined"
            sx={{
              borderColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#fff' : '#000',
              '&:hover': {
                borderColor: isDarkMode ? '#e0e0e0' : '#333',
                backgroundColor: 'transparent',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaidConfirmDialogConfirm}
            disabled={batchPaidUpdating}
            variant="contained"
            sx={{
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : '#fff',
              '&:hover': {
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              },
            }}
          >
            {batchPaidUpdating ? 'Updating...' : `Confirm (${pendingPaidJobIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isNotesDialogOpen}
        onClose={handleCloseNotesDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            backgroundColor: 'background.paper',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            value={notesDraft}
            onChange={(event) => setNotesDraft(event.target.value)}
            placeholder="Enter job notes..."
            disabled={notesUpdating}
            error={Boolean(notesDialogError)}
            helperText={notesDialogError || `${notesDraft.length}/500`}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseNotesDialog}
            disabled={notesUpdating}
            variant="outlined"
            sx={{
              borderColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#fff' : '#000',
              '&:hover': {
                borderColor: isDarkMode ? '#e0e0e0' : '#333',
                backgroundColor: 'transparent',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateNotes}
            disabled={notesUpdating || notesDraft.length > 500}
            variant="contained"
            sx={{
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : '#fff',
              '&:hover': {
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              },
            }}
          >
            {notesUpdating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Accounting;
